import { useEffect, useState } from "react";
import { Plus, FileText, ListChecks, RefreshCw } from "lucide-react";
import { adminQuotes, STATUS_LABEL, eur } from "../../../lib/quotesApi";
import { QuoteEditor } from "./QuoteEditor";
import { QuoteCatalogEditor } from "./QuoteCatalogEditor";

const FILTERS = [["", "Tutti"], ["request", "Richieste"], ["draft", "Bozze"], ["sent", "Inviati"], ["viewed", "Visti"], ["accepted", "Accettati"], ["deposit_paid", "Caparra pagata"], ["declined", "Rifiutati"]];

export function QuotesPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // null | "new-stay" | "new-event" | quote
  const [catalog, setCatalog] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); try { setItems(await adminQuotes()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const shown = items.filter((q) => !filter || q.status === filter);
  const counts = items.reduce((a, q) => ({ ...a, [q.status]: (a[q.status] || 0) + 1 }), {});

  if (catalog) return <QuoteCatalogEditor onBack={() => setCatalog(false)} />;
  if (editing) return <QuoteEditor quote={typeof editing === "string" ? null : editing} kind={typeof editing === "string" ? editing.replace("new-", "") : editing.kind} onBack={() => { setEditing(null); load(); }} />;

  return (
    <div className="space-y-4" data-testid="quotes-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-2xl font-bold text-[#2b1410] mr-auto">Preventivi</h2>
        <button onClick={() => setCatalog(true)} className="h-9 px-3 rounded-full border border-[#e8dcc8] bg-white text-sm inline-flex items-center gap-1.5" data-testid="quotes-catalog-btn"><ListChecks className="h-4 w-4" /> Listino voci</button>
        <button onClick={load} className="h-9 w-9 rounded-full border border-[#e8dcc8] bg-white inline-flex items-center justify-center" data-testid="quotes-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        <button onClick={() => setEditing("new-stay")} className="h-9 px-3 rounded-full bg-[#4a1c17] text-white text-sm font-semibold inline-flex items-center gap-1.5" data-testid="quotes-new-stay"><Plus className="h-4 w-4" /> Soggiorno</button>
        <button onClick={() => setEditing("new-event")} className="h-9 px-3 rounded-full bg-[#C48B31] text-white text-sm font-semibold inline-flex items-center gap-1.5" data-testid="quotes-new-event"><Plus className="h-4 w-4" /> Evento</button>
      </div>
      <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none]">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold ${filter === k ? "bg-[#4a1c17] text-white" : "bg-white border border-[#e8dcc8] text-[#4a1c17]"}`} data-testid={`quotes-filter-${k || "all"}`}>
            {label}{k && counts[k] ? ` · ${counts[k]}` : ""}
          </button>
        ))}
      </div>
      {shown.length === 0 && !loading && (
        <div className="rounded-2xl border border-dashed border-[#e8dcc8] bg-white p-10 text-center text-sm text-[#7c5a4e]" data-testid="quotes-empty">
          <FileText className="h-8 w-8 mx-auto mb-2 text-[#C48B31]" /> Nessun preventivo qui. Creane uno con "Soggiorno" o "Evento": l'AI ti prepara la bozza dal brief.
        </div>
      )}
      <ul className="space-y-2">
        {shown.map((q) => {
          const [label, cls] = STATUS_LABEL[q.status] || [q.status, ""];
          return (
            <li key={q.id}>
              <button onClick={() => setEditing(q)} className="w-full text-left rounded-2xl bg-white border border-[#e8dcc8] p-4 hover:border-[#C48B31] transition flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`quote-row-${q.number}`}>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${cls}`}>{label}</span>
                <span className="text-xs text-[#7c5a4e] font-mono">{q.number}</span>
                <span className="text-xs rounded-full bg-[#faf3ee] px-2 py-0.5">{q.kind === "stay" ? "Soggiorno" : "Evento"}</span>
                <span className="font-semibold text-[#2b1410] flex-1 min-w-[160px] truncate">{q.customer?.name || "—"} {q.title ? <span className="font-normal text-[#7c5a4e]">· {q.title}</span> : null}</span>
                {q.request?.text && q.status === "request" && <span className="w-full text-xs text-[#7c5a4e] truncate">"{q.request.text}"</span>}
                {q.request?.date_from && <span className="text-xs text-[#7c5a4e]">📅 {q.request.date_from}{q.request.date_to ? ` → ${q.request.date_to}` : ""}</span>}
                <span className="font-bold tabular-nums">{q.total_cents ? eur(q.total_cents) : ""}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
